import { UseFormSetValue } from 'react-hook-form';
import studentProfile from '../../../apis/studentProfile';
import { SnackbarSeverityType } from '../../../enums';
import { StudentDetailProps } from '../types/studentDetailType';
import { uploadPhoto } from '../utils/uploadPhoto';
import { downloadFileFromUrl } from '../../../utils/fileGeneration';

export const useStudentActions = (
  studentId: string,
  setValue: UseFormSetValue<StudentDetailProps>,
  showToast: (message: string, severity: SnackbarSeverityType) => void,
  handleCloseGenerateLcModal: () => void
) => {
  const uploadStudentPhoto = async (file: File | null) => {
    if (!file) return;

    const response = await uploadPhoto(file, studentId, false);

    if (response && response.errors) {
      showToast(
        response?.errors?.[0].message || 'Failed to upload profile',
        SnackbarSeverityType.error
      );
      return;
    }

    if (response) {
      setValue('studentDetails.profile', response.uploadedPath);
      showToast('Successfully uploaded profile', SnackbarSeverityType.success);
    }
  };

  const acceptRejectLCRequest = async (status: 'REJECTED' | 'ACCEPTED') => {
    try {
      await studentProfile.acceptRejectStudentLC(studentId, status);
      showToast(
        `LC request ${
          status === 'ACCEPTED' ? 'accepted' : 'rejected'
        } successfully`,
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          `Failed to ${status === 'ACCEPTED' ? 'accept' : 'reject'} LC request`,
        SnackbarSeverityType.error
      );
    }
  };

  const generateBonafide = async () => {
    try {
      const response = await studentProfile.generateBonafide(studentId);
      if (response) {
        showToast(
          'Bonafide generated successfully',
          SnackbarSeverityType.success
        );
      } else {
        showToast('Failed to generate bonafide', SnackbarSeverityType.error);
      }
    } catch (error) {
      showToast('Failed to generate bonafide', SnackbarSeverityType.error);
    }
  };

  const activateStudent = async () => {
    try {
      await studentProfile.activateStudent(studentId);
      showToast('Student activated successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to activate student',
        SnackbarSeverityType.error
      );
    }
  };

  const deActivateStudent = async () => {
    try {
      await studentProfile.deActivateStudent(studentId);
      showToast(
        'Student deactivated successfully',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to deactivate student',
        SnackbarSeverityType.error
      );
    }
  };

  const generateLC = async (data: any) => {
    try {
      const response = await studentProfile.generateLC(studentId, data);
      const studentLCPdfUrl = response as any;
      if (studentLCPdfUrl) {
        const pdfUrl = studentLCPdfUrl.lcPdfUrl;
        const fileName = studentLCPdfUrl.fileName;

        downloadFileFromUrl(pdfUrl, fileName, 'pdf');

      }
      showToast('LC generated successfully', SnackbarSeverityType.success);
      handleCloseGenerateLcModal();
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to generate LC',
        SnackbarSeverityType.error
      );
    }
  };

  const requestLC = async () => {
    try {
      await studentProfile.requestLC(studentId);
      showToast('LC request sent successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to send LC request',
        SnackbarSeverityType.error
      );
    }
  };

  return {
    uploadStudentPhoto,
    acceptRejectLCRequest,
    generateBonafide,
    activateStudent,
    deActivateStudent,
    generateLC,
    requestLC
  };
};
